import React, { useEffect, useMemo, useState } from 'react'
import { FiSettings } from 'react-icons/fi'
import MeunSideSetting from '../components/settings/MeunSideSetting'
import { MwButton } from '../components/ui'
import { useCurrent, useFetch, usePut } from '../hooks'
import { useTranslation } from 'react-i18next'

const Settings = () => {
    const [activeMenu, setActiveMenu] = useState('ServicesLanguage')
    const [servicesDefaultLanguage, setServicesDefaultLanguage] = useState('ar')
    const [badRatingEmailEnabled, setBadRatingEmailEnabled] = useState(false)
    const [badRatingEmail, setBadRatingEmail] = useState('')
    const [isDirty, setIsDirty] = useState(false)
    const [formError, setFormError] = useState('')
    const { currentTenantId, currentCompanyId, currentBranchId, user } = useCurrent()
    const [t] = useTranslation('global')
    const settingsUrl = useMemo(() => {
        if (!currentTenantId || !currentCompanyId || !currentBranchId) return null
        return `${process.env.REACT_APP_RATING_API_BASE_URL}/services/settings/language/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
    }, [currentTenantId, currentCompanyId, currentBranchId])
    const { data: settingsResult, loading: settingsLoading, refreshHandler } = useFetch(settingsUrl || '', !!settingsUrl)
    const { data: putResult, loading: putLoading, putData } = usePut()

    useEffect(() => {
        const nextLanguage = settingsResult?.data?.servicesDefaultLanguage
        if (nextLanguage === 'ar' || nextLanguage === 'en') {
            setServicesDefaultLanguage(nextLanguage)
        }
        setBadRatingEmailEnabled(Boolean(settingsResult?.data?.badRatingEmailEnabled))
        setBadRatingEmail(String(settingsResult?.data?.badRatingEmail || ''))
        setFormError('')
        setIsDirty(false)
    }, [settingsResult])

    const saveSettingsHandler = () => {
        if (!settingsUrl) return
        setFormError('')
        const localStorageUser = (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')
            } catch {
                return {}
            }
        })()
        const fallbackEmail = String(
            badRatingEmail || user?.email || localStorageUser?.email || ''
        ).trim().toLowerCase()
        if (badRatingEmailEnabled && !fallbackEmail) {
            setFormError(t('settings.badRatingEmailRequired'))
            return
        }
        putData(settingsUrl, {
            servicesDefaultLanguage,
            badRatingEmailEnabled,
            badRatingEmail: fallbackEmail
        })
    }

    useEffect(() => {
        if (putResult?.status === 1 && isDirty) {
            setIsDirty(false)
            refreshHandler()
        }
    }, [putResult, refreshHandler, isDirty])

    return (
        <>
            <div className='flex justify-between items-center pb-5 pt-2'>
                <div className=' flex gap-1  text-sm text-gray-400 '>
                    <FiSettings size={18} /> 
                    {t('settings.title')}
                </div>
            </div>
            <div className='flex gap-2 mb-5'>
                <MeunSideSetting activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
                <div className='flex-1 bg-white border border-slate-200 rounded-lg p-4 overflow-auto h-[calc(100vh-200px)]'>
                    {activeMenu === 'ServicesLanguage' && (
                        <div className='max-w-xl rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4'>
                            <div className='text-sm font-bold text-slate-700'>{t('settings.servicesLanguage')}</div>
                            <div className='text-xs text-slate-500'>{t('settings.servicesLanguageHint')}</div>
                            <select
                                className='h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none'
                                value={servicesDefaultLanguage}
                                disabled={settingsLoading || putLoading}
                                onChange={(event) => {
                                    setServicesDefaultLanguage(event.target.value)
                                    setIsDirty(true)
                                }}
                            >
                                <option value='ar'>{t('services.defaultLanguageAr')}</option>
                                <option value='en'>{t('services.defaultLanguageEn')}</option>
                            </select>
                            <hr className='border-slate-200' />
                            <div className='text-sm font-bold text-slate-700'>{t('settings.badRatingNotification')}</div>
                            <div className='text-xs text-slate-500'>{t('settings.badRatingNotificationHint')}</div>
                            <div className='flex items-center justify-between'>
                                <span className='text-sm text-slate-700'>{t('settings.enableBadRatingNotification')}</span>
                                <button
                                    type='button'
                                    disabled={settingsLoading || putLoading}
                                    onClick={() => {
                                        setBadRatingEmailEnabled((prev) => !prev)
                                        setIsDirty(true)
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${badRatingEmailEnabled ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${badRatingEmailEnabled ? 'translate-x-5' : 'translate-x-1'}`}
                                    />
                                </button>
                            </div>
                            <input
                                type='email'
                                className='h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none disabled:bg-slate-100'
                                value={badRatingEmail}
                                placeholder={t('settings.badRatingEmailPlaceholder')}
                                disabled={settingsLoading || putLoading || !badRatingEmailEnabled}
                                onChange={(event) => {
                                    setBadRatingEmail(event.target.value)
                                    setIsDirty(true)
                                }}
                            />
                            {!!formError && <div className='text-xs text-red-500'>{formError}</div>}
                            <div className='flex justify-end'>
                                <MwButton
                                    type='saveBtn'
                                    disabled={settingsLoading || putLoading || !isDirty}
                                    onClick={saveSettingsHandler}
                                >
                                    {t('common.save')}
                                </MwButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )

}

export default Settings
